import React, { useMemo, useState } from "react";
import { BUILDER_V2_TOOL_DISPLAY_NAMES } from "common/utils/chatAgent/builderTools";
import {
  type BuilderToolResult,
  builderToolResultSchema,
  type ToolActivity,
} from "common/utils/chatAgent/types";
import { Icon as LucideIcon } from "../../../Common/Icons/LucideIcons";
import {
  Actions,
  Card,
  Details,
  ErrorText,
  FieldName,
  FieldRow,
  FieldValue,
  Header,
  Meta,
  SmallButton,
  StatusPill,
  Summary,
  Title,
  TitleRow,
} from "../__styles__/ToolActivityCard";

function formatToolName(name: string): string {
  if (name in BUILDER_V2_TOOL_DISPLAY_NAMES) {
    return BUILDER_V2_TOOL_DISPLAY_NAMES[
      name as keyof typeof BUILDER_V2_TOOL_DISPLAY_NAMES
    ];
  }
  return name
    .split("_")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function extractFieldMetadata(
  result: BuilderToolResult,
): Array<{ path: string; value: unknown }> {
  const entries: Array<{ path: string; value: unknown }> = [];

  const skipKeys = new Set([
    "formStructure",
    "currentFields",
    "error",
    "action",
    "summary",
  ]);

  for (const [key, value] of Object.entries(result || {})) {
    if (skipKeys.has(key)) continue;

    if (Array.isArray(value)) {
      entries.push({ path: key, value: value.join(", ") });
    } else if (typeof value === "object" && value !== null) {
      for (const [nestedKey, nestedValue] of Object.entries(value)) {
        entries.push({
          path: `${key}.${nestedKey}`,
          value: nestedValue,
        });
      }
    } else {
      entries.push({ path: key, value });
    }
  }

  return entries;
}

function truncateString(str: string, max = 160): string {
  if (str.length <= max) return str;
  return `${str.slice(0, max)}…`;
}

function toDisplayValue(value: unknown): string {
  try {
    if (typeof value === "string") return truncateString(value);
    return truncateString(JSON.stringify(value));
  } catch {
    return String(value);
  }
}

export interface BuilderToolActivityCardProps {
  activity: ToolActivity;
}

const BuilderToolActivityCard: React.FC<BuilderToolActivityCardProps> = ({
  activity,
}) => {
  const [expanded, setExpanded] = useState(false);

  const displayName = useMemo(
    () => formatToolName(activity.name || "Tool"),
    [activity.name],
  );

  const validatedResult = useMemo(() => {
    if (!activity.result) {
      return null;
    }
    const parseResult = builderToolResultSchema.safeParse(activity.result);
    if (parseResult.success) {
      return parseResult.data;
    }

    console.warn("Builder tool result validation failed", {
      error: parseResult.error,
      rawResult: activity.result,
      activityName: activity.name,
      toolCallId: activity.toolCallId,
    });
    return activity.result as BuilderToolResult;
  }, [activity.result, activity.name, activity.toolCallId]);

  const fields = useMemo(
    () => extractFieldMetadata(validatedResult || ({} as BuilderToolResult)),
    [validatedResult],
  );

  const durationLabel =
    activity.durationMs != null ? `${activity.durationMs} ms` : undefined;

  const summary = useMemo(() => {
    return (
      activity.summary ||
      (validatedResult && "summary" in validatedResult ?
        validatedResult.summary
      : undefined)
    );
  }, [activity.summary, validatedResult]);

  const copyResult = async () => {
    try {
      await navigator.clipboard.writeText(
        JSON.stringify(activity.result ?? {}, null, 2),
      );
    } catch (error) {
      console.error("Failed to copy tool result to clipboard", {
        error,
        activityName: activity.name,
        toolCallId: activity.toolCallId,
        result: activity.result,
      });
    }
  };

  return (
    <Card aria-live="polite">
      <Header>
        <TitleRow>
          <LucideIcon iconName="component" size={14} color="contentPrimary" />
          <Title>{displayName}</Title>
          <StatusPill status={activity.status as any} role="status">
            {activity.status === "completed" && "Completed"}
            {activity.status === "running" && "Running"}
            {activity.status === "failed" && "Failed"}
          </StatusPill>
        </TitleRow>
        <Actions>
          <SmallButton
            onClick={() => setExpanded(prev => !prev)}
            aria-expanded={expanded}
            aria-controls={`tool-activity-details-${activity.startedAt}`}
          >
            {expanded ? "Hide details" : "Show details"}
          </SmallButton>
          <SmallButton onClick={copyResult} disabled={!activity.result}>
            Copy
          </SmallButton>
        </Actions>
      </Header>

      {summary && <Summary>{summary}</Summary>}
      {activity.status === "failed" && activity.errorMessage && (
        <ErrorText>{activity.errorMessage}</ErrorText>
      )}
      <Meta>
        {durationLabel && (
          <>
            <LucideIcon iconName="clock" size={12} color="contentSecondary" />
            <span>{durationLabel}</span>
          </>
        )}
      </Meta>

      {expanded && (
        <Details id={`tool-activity-details-${activity.startedAt}`}>
          {fields.length === 0 ?
            <FieldName>No changes</FieldName>
          : fields.slice(0, 50).map(({ path, value }) => (
              <FieldRow key={path}>
                <FieldName>{path}</FieldName>
                <FieldValue>{toDisplayValue(value)}</FieldValue>
              </FieldRow>
            ))
          }
          {fields.length > 50 && (
            <FieldName>{`…and ${fields.length - 50} more`}</FieldName>
          )}
        </Details>
      )}
    </Card>
  );
};

export default BuilderToolActivityCard;
